import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DashboardTopBarAdmin from "../../components/admin/DashboardTopBarAdmin";
import PrimaryButton from "../../components/shared/PrimaryButton";
import SuccessButton from "../../components/shared/SuccessButton";
import ErrorButton from "../../components/shared/ErrorButton";
import Modal from "../../components/shared/Modal";
import LoadingIndicatorButton from "../../components/shared/LoadingIndicatorButton";
import CollectionFormAdmin from "../../components/admin/collections/CollectionFormAdmin";
import { fetchCollections, deleteCollection, updateCollectionSortOrder } from "../../util/admin/httpAdmin";
import dragHandleIcon from "../../assets/admin/icons/drag-handle.svg";
import collectionsBlueIcon from "../../assets/admin/icons/collections-blue.svg";
import plusIcon from "../../assets/admin/icons/plus-white.svg";
import styles from "./CollectionsAdmin.module.css";

function CollectionsAdmin() {
    const modalRef = useRef();
    const deleteModalRef = useRef();
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [collectionToDelete, setCollectionToDelete] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const queryClient = useQueryClient();

    const openModal = () => modalRef.current.open();
    const closeModal = () => {
        modalRef.current.close();
        setTimeout(() => setSelectedCollection(null), 500);
    };

    const openDeleteModal = (collection) => {
        setCollectionToDelete(collection);
        deleteModalRef.current.open();
    };

    const closeDeleteModal = () => {
        deleteModalRef.current.close();
        setCollectionToDelete(null);
    };

    const handleEditButtonClick = (collection) => {
        setSelectedCollection(collection);
        openModal();
    };

    const { data: collections, isPending, isError, error } = useQuery({
        queryKey: ["adminCollections"],
        queryFn: ({ signal }) => fetchCollections({ signal }),
    });

    const deleteMutation = useMutation({
        mutationFn: deleteCollection,
        onSuccess: () => {
            queryClient.invalidateQueries(["adminCollections"]);
            closeDeleteModal();
        },
    });

    const sortMutation = useMutation({
        mutationFn: updateCollectionSortOrder,
        onSuccess: () => {
            queryClient.invalidateQueries(["adminCollections"]);
        },
    });

    const handleDragStart = (e, collection) => {
        setDraggedItem(collection);
        e.dataTransfer.effectAllowed = 'move';
        e.target.classList.add(styles.dragging);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnd = (e) => {
        e.target.classList.remove(styles.dragging);
        setDraggedItem(null);
    };

    const handleDrop = (e, targetCollection) => {
        e.preventDefault();
        if (!draggedItem || draggedItem._id === targetCollection._id) return;

        const items = Array.from(collections);
        const draggedIndex = items.findIndex(item => item._id === draggedItem._id);
        const targetIndex = items.findIndex(item => item._id === targetCollection._id);

        items.splice(draggedIndex, 1);
        items.splice(targetIndex, 0, draggedItem);

        sortMutation.mutate({ collections: items });
        setDraggedItem(null);
    };

    return (
        <div>
            <DashboardTopBarAdmin
                title="Collecties"
                icon={collectionsBlueIcon}
                description="Hier kan je al jouw collecties bekijken en bewerken"
            >
                <div>
                    <PrimaryButton onClick={() => {
                        setSelectedCollection(null);
                        openModal();
                    }}>
                        <div className="g-15-1fr">
                            <img src={plusIcon} alt="add" />
                            <span>Nieuwe Collectie</span>
                        </div>
                    </PrimaryButton>
                </div>
            </DashboardTopBarAdmin>

            <div className="py-20 px-20">
                {isPending && <LoadingIndicatorButton dark />}
                {isError && <p className="c-r">{error?.message || "Er is iets foutgegaan"}</p>}

                {collections && (
                    <div className="g-20">
                        <table className="w-100">
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}></th>
                                    <th>Icoon</th>
                                    <th>Naam</th>
                                    <th>Acties</th>
                                </tr>
                            </thead>
                            <tbody>
                                {collections.map((collection) => (
                                    <tr
                                        key={collection._id}
                                        className={styles.draggableRow}
                                    >
                                        <td 
                                            className={styles.dragHandle}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, collection)}
                                            onDragOver={handleDragOver}
                                            onDragEnd={handleDragEnd}
                                            onDrop={(e) => handleDrop(e, collection)}
                                        >
                                            <img src={dragHandleIcon} alt="Sleep" />
                                        </td>
                                        <td className="text-center">
                                            <img 
                                                src={collection.icon} 
                                                alt={collection.name}
                                                style={{ width: '24px', height: '24px', verticalAlign: 'middle' }}
                                            />
                                        </td>
                                        <td>{collection.name}</td>
                                        <td>
                                            <div className="flex-2">
                                                <SuccessButton onClick={() => handleEditButtonClick(collection)}>
                                                    Bewerken
                                                </SuccessButton>
                                                <ErrorButton onClick={() => openDeleteModal(collection)}>
                                                    Verwijderen
                                                </ErrorButton>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal ref={modalRef}>
                <CollectionFormAdmin
                    key={selectedCollection ? selectedCollection._id : "new"}
                    onSuccessClose={closeModal}
                    editMode={!!selectedCollection}
                    previousData={selectedCollection || {}}
                />
            </Modal>

            <Modal ref={deleteModalRef}>
                <div className="py-20 px-20 g-20">
                    <h2>Bevestig Verwijderen</h2>
                    <p>Weet je zeker dat je de collectie "{collectionToDelete?.name}" wilt verwijderen?</p>
                    <div className="flex-2">
                        <ErrorButton
                            onClick={() => deleteMutation.mutate(collectionToDelete._id)}
                            disabled={deleteMutation.isPending}
                        >
                            Verwijderen
                        </ErrorButton>
                        <PrimaryButton className="secondary-button" onClick={closeDeleteModal}>
                            Annuleren
                        </PrimaryButton>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default CollectionsAdmin; 